import { RightOutlined } from '@ant-design/icons';
import useMobileView from '@design-system/hooks/useMobileView';
import { Col, Divider, Row, Spin } from 'antd';
import { useRouter } from 'next/router';
import React, { FC, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { TLanguageKey } from '@source/interface';
import { useTranslation } from 'react-i18next';

import useRegionConfig from '@source/hooks/useRegionConfig';
import useBrandsServices, { BrandItem } from '@source/hooks/useBrandsServices';
import i18next from 'i18next';

const StyledDropdownContainer = styled.div`
  background-color: ${(props) => props.theme.color.background};
  border: 1px solid ${(props) => props.theme.color.neutral300 || '#d1d5db'};
  border-radius: 12px;
  padding: 15px 20px 25px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: fit-content;
  min-width: 288px;
  max-width: 288px;
  max-height: 660px;
  overflow-y: auto;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    max-width: none;
  }

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.color.neutral300 || '#d1d5db'};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.color.neutral400 || '#9ca3af'};
  }

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    gap: 15px;
  }
`;

const StyledSectionLabel = styled.p`
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-size: 10px;
  line-height: normal;
  color: ${(props) => props.theme.color.primaryBase};
  margin: 0;
`;

const StyledBrandsRow = styled(Row)`
  padding: 0 11px;
`;

const StyledCol = styled(Col)<{ $isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  ${(props) => props.theme.typo.familyGoogle.regular};
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => props.theme.color.onBackgroundHighEmphasis};

  ${(props) =>
    !props.$isMobile &&
    `
    min-width: 115px;
  `}
`;

const StyledBrandItem = styled.a<{ $isActive?: boolean }>`
  margin: 0;
  height: 20px;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  color: ${(props) => (props.$isActive ? props.theme.color.primaryBase : 'inherit')};
  font-weight: ${(props) => (props.$isActive ? 600 : 'inherit')};

  &:hover {
    color: ${(props) => props.theme.color.primaryBase};
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0;
  border-color: ${(props) => props.theme.color.neutral300 || '#bcbcbd'};
`;

const StyledViewAllContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 15px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const StyledViewAllText = styled.p`
  ${(props) => props.theme.typo.familyGoogle.semiBold};
  font-size: 14px;
  line-height: 22px;
  color: ${(props) => props.theme.color.primaryBase};
  margin: 0;
`;

const StyledArrowIcon = styled(RightOutlined)`
  font-size: 10px;
  color: ${(props) => props.theme.color.primaryBase};
`;

const BuyDropDownMenu: FC = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const isMobile = useMobileView(768);
  const device = isMobile ? 'mobile' : 'desktop';
  const {
    countryConfig: { country: region = 'sg' },
  } = useRegionConfig();

  const language = i18next.language as TLanguageKey;
  const { makes, loading, fetchMakes, getDropdownCategories } = useBrandsServices();

  useEffect(() => {
    fetchMakes();
  }, [fetchMakes]);

  const categories = useMemo(() => {
    return getDropdownCategories(makes, language, device);
  }, [makes, language, device, getDropdownCategories]);

  const colSpan = isMobile ? 12 : 8;

  const handleViewAllClick = () => {
    router.push(`/${region}/${language}`);
  };

  if (loading && makes.length === 0) {
    return (
      <StyledDropdownContainer>
        <Spin />
      </StyledDropdownContainer>
    );
  }

  return (
    <StyledDropdownContainer>
      {Object.entries(categories).map(([key, category], index) => (
        <React.Fragment key={key}>
          {index > 0 && <StyledDivider />}
          <StyledSectionLabel>{t(category.label)}</StyledSectionLabel>
          <StyledBrandsRow gutter={15}>
            {category.columns.map((column: BrandItem[], colIndex: number) => (
              <StyledCol key={colIndex} span={colSpan} $isMobile={isMobile}>
                {column.map((brand: BrandItem) => (
                  <StyledBrandItem key={brand.label} href={brand.url} $isActive={router.asPath.includes(brand.slug)}>
                    {brand.label}
                  </StyledBrandItem>
                ))}
              </StyledCol>
            ))}
          </StyledBrandsRow>
        </React.Fragment>
      ))}

      <StyledViewAllContainer onClick={handleViewAllClick}>
        <StyledViewAllText>{t('shared.topNavigation.buyDropdown.viewAllCars')}</StyledViewAllText>
        <StyledArrowIcon />
      </StyledViewAllContainer>
    </StyledDropdownContainer>
  );
};

export default BuyDropDownMenu;
